import type { ITeacher } from '@/types/teacher';

import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  IconBook,
  IconUsers,
  IconLicense,
  IconUserCheck,
} from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import EmptyState from '@/components/EmptyState';
import ProgressBar from '@/components/ProgressBar';
import DashboardSkeleton from '@/components/SkeletonLoader/DashboardSkeleton';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  getSchoolDashboardSummary,
  getSchoolDashboardTopClasses,
} from '@/service/dashboardService';
import { getTeacherList } from '@/service/teacherService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { getAssetUrl, formatLastActive } from '@/utils';

import TopClassesChart from './components/TopClassesChart';

// ─── Mock Data ───────────────────────────────────────────────────────────────

interface ITopClassApiItem {
  id: string;
  name: string;
  completed_lessons: number;
}

interface ISummaryMetric {
  total: number;
  growth: number;
  trend: 'UP' | 'DOWN' | 'STABLE';
}

interface ISummaryApiData {
  total_teachers: ISummaryMetric;
  total_students: ISummaryMetric;
  assigned_lessons: ISummaryMetric;
  license_usage: {
    used: number;
    total: number;
  };
}

const Dashboard = () => {
  const { formatText } = useFormatText();
  const navigate = useNavigate();
  const setTitle = usePageTitle((state) => state.setTitle);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { isScreenLoading, isTableLoading, setScreenLoading, setTableLoading } =
    useSkeletonStore();
  const [teachers, setTeachers] = useState<ITeacher[]>([]);
  const [teacherTotal, setTeacherTotal] = useState(0);
  const [summaryData, setSummaryData] = useState<ISummaryApiData | null>(null);
  const [topClasses, setTopClasses] = useState<ITopClassApiItem[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ASC' | 'DESC';
  } | null>(null);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'ASC' ? 'DESC' : 'ASC' };
      }
      return { key, direction: 'ASC' };
    });
  };

  const handleRowClick = (id: string) => {
    navigate(`/school/teachers-classes/teacher-details/${id}`);
  };

  // Set page title
  setTitle(formatText('dashboard.title'));

  // ── Fetch dashboard data ──────────────────────────────────────────────────

  useEffect(() => {
    const fetchDashboardData = async () => {
      setScreenLoading(true);
      try {
        // Fetch top classes and summary from the API in parallel
        const [topClassesResponse, summaryResponse] = await Promise.all([
          getSchoolDashboardTopClasses(),
          getSchoolDashboardSummary(),
        ]);

        if (
          topClassesResponse?.status &&
          Array.isArray(topClassesResponse.data)
        ) {
          setTopClasses(topClassesResponse.data);
        }

        if (summaryResponse?.status && summaryResponse.data) {
          setSummaryData(summaryResponse.data);
        }
      } finally {
        setScreenLoading(false);
      }
    };

    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTeachers = async () => {
    setTableLoading(true);
    try {
      const query: Record<string, unknown> = {
        offset: (currentPage - 1) * pageSize,
        limit: pageSize,
      };
      if (sortConfig?.key && sortConfig?.direction) {
        query[`order[${sortConfig.key}]`] = sortConfig.direction;
      }

      const res = await getTeacherList(query);
      if (res?.data) {
        setTeachers(res.data ?? []);
        setTeacherTotal(res.total ?? res.data.length ?? 0);
      }
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sortConfig]);

  // ── Empty state check ────────────────────────────────────────────────────

  const isDashboardEmpty =
    !isScreenLoading && teachers.length === 0 && !summaryData;

  // ─── Teacher Table Columns ───────────────────────────────────────────────────

  const teacherColumns = useMemo(
    () => [
      {
        key: 'name',
        title: 'dashboard.table.teacher-name',
        width: '200px',
        sorter: true,
        handleSort: () => handleSort('name'),
        render: (record: ITeacher) => (
          <div className="flex items-center gap-3">
            <UserAvatarList
              user={record.name}
              image={getAssetUrl(record?.profile_pic)}
              className="w-10 h-10 min-w-10"
            />
            <span className="text-sm font-medium text-neutral-primary truncate max-w-40">
              {record.name}
            </span>
          </div>
        ),
      },
      {
        key: 'email',
        title: 'dashboard.table.email',
        width: '220px',
        sorter: true,
        handleSort: () => handleSort('email'),
        render: (record: ITeacher) => (
          <span className="text-sm text-neutral-secondary">{record.email}</span>
        ),
      },
      {
        key: 'total_classes',
        title: 'dashboard.table.classes',
        align: 'start' as const,
        width: '100px',
        sorter: true,
        handleSort: () => handleSort('total_classes'),
        render: (record: ITeacher) => (
          <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-sm font-medium">
            {record.total_classes}
          </span>
        ),
      },
      {
        key: 'total_lessons',
        title: 'dashboard.table.lessons-created',
        align: 'start' as const,
        width: '140px',
        sorter: true,
        handleSort: () => handleSort('total_lessons'),
        render: (record: ITeacher) => (
          <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-pink-50 text-pink-600 text-sm font-medium">
            {record.total_lessons}
          </span>
        ),
      },
      {
        key: 'active_students',
        title: 'dashboard.table.active-students',
        align: 'start' as const,
        width: '130px',
        sorter: true,
        handleSort: () => handleSort('active_students'),
        render: (record: ITeacher) => (
          <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-green-50 text-green-600 text-sm font-medium">
            {record.active_students}
          </span>
        ),
      },
      {
        key: 'avg_completion',
        title: 'dashboard.table.avg-completion',
        width: '180px',
        sorter: true,
        handleSort: () => handleSort('avg_completion'),
        render: (record: ITeacher) => (
          <div className="flex flex-col gap-1">
            <ProgressBar value={record.avg_completion ?? 0} />
          </div>
        ),
      },
      {
        key: 'last_update',
        title: 'dashboard.table.last-update',
        width: '120px',
        sorter: true,
        handleSort: () => handleSort('last_update'),
        render: (record: ITeacher) => (
          <span className="text-sm text-neutral-secondary whitespace-nowrap">
            {formatLastActive(
              record.last_update ? Number(record.last_update) : 0,
              formatText
            )}
          </span>
        ),
      },
    ],
    [formatText]
  );

  // ── Render content based on loading / empty state ────────────────────────

  const getDashboardContent = () => {
    if (isScreenLoading) {
      return <DashboardSkeleton />;
    }

    if (isDashboardEmpty) {
      return (
        <EmptyState
          title={formatText('dashboard.empty-title')}
          description={formatText('dashboard.empty-description')}
          dataTestId="dashboard-empty-state"
        />
      );
    }

    return (
      <>
        {/* Dashboard Metrics */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
          data-testid="dashboard-metrics"
        >
          <SummaryCard
            variant="dashboard"
            label={formatText('dashboard.metrics.total-teachers')}
            value={summaryData ? summaryData.total_teachers.total : 0}
            icon={<IconUsers size={24} className="text-primary-default" />}
            dataTestId="summary-total-teachers"
          />
          <SummaryCard
            variant="dashboard"
            label={formatText('dashboard.metrics.total-students')}
            value={summaryData ? summaryData.total_students.total : 0}
            icon={
              <IconUserCheck size={24} className="text-secondary-default" />
            }
            dataTestId="summary-total-students"
          />
          <SummaryCard
            variant="dashboard"
            label={formatText('dashboard.metrics.assigned-lessons')}
            value={
              summaryData
                ? summaryData.assigned_lessons.total.toLocaleString()
                : '0'
            }
            icon={<IconBook size={24} className="text-success-default" />}
            dataTestId="summary-assigned-lessons"
          />
          <SummaryCard
            variant="dashboard"
            label={formatText('dashboard.metrics.license-usage')}
            value={
              summaryData?.license_usage
                ? `${summaryData?.license_usage?.used}/${summaryData?.license_usage?.total}`
                : '0/0'
            }
            icon={<IconLicense size={24} className="text-warning-default" />}
            dataTestId="summary-license-usage"
          />
        </div>

        {/* Top 5 Classes Bar Chart */}
        <div
          className="bg-card rounded-xl border border-neutral-light-200 shadow-2xs p-4"
          data-testid="top-classes-chart-container"
        >
          <h2 className="text-lg font-semibold text-neutral-primary mb-4">
            {formatText('dashboard.chart.title')}
          </h2>
          <TopClassesChart data={topClasses} />
        </div>

        {/* Teacher Overview Table */}
        <div className="bg-card" data-testid="teacher-overview-table-container">
          <h2 className="text-lg font-semibold text-neutral-primary mb-4">
            {formatText('dashboard.table.title')}
          </h2>
          <CustomTable
            dataSource={teachers}
            columns={teacherColumns}
            rowKey="id"
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            total={teacherTotal}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            onRowClick={handleRowClick}
            isLoading={isTableLoading}
            fieldName={sortConfig?.key}
            sortDirection={sortConfig?.direction}
            dataTestId="teacher-overview-table"
          />
        </div>
      </>
    );
  };

  return <div className="flex flex-col gap-6">{getDashboardContent()}</div>;
};

export default Dashboard;
